import {
  ActionName,
  CategoryName,
  IContextMenuItemRefreshRules,
} from '@infotech-soe/metaschema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { TActionArgsValues } from '../../infrastructure/providers/abstract/IApiProvider';
import { DatasetGridViewModel } from '../../models/dataset/DatasetRelated';
import CategoryViewModel from '../../services/category/view-model/CategoryViewModel';
import { RefreshStrategy } from '../../services/commands/wrap/CategoryCommand';
import {
  ClientContext,
  ClientContextKeys,
} from '../../services/context/IContextService';
import { CategoryFormSource } from '../../services/form/view-model/FormSource';
import UniqueId from '../../services/uid/UniqueId';

import {
  emptyAction,
  failureAction,
  pendingAction,
  successAction,
} from './actionHandler';

export interface ActionsState {
  readonly processState: Record<string, any>;
}

const initialState: ActionsState = {
  processState: {},
};

const actionsSlice = createSlice({
  name: 'actions',
  initialState,
  reducers: {
    updateProcessState(
      state,
      {
        payload: { categoryId, processState },
      }: PayloadAction<{
        categoryId: UniqueId;
        processState: Record<string, any>;
      }>
    ) {
      state.processState[categoryId] = processState;
    },
    processMultistep: pendingAction<
      ActionsState,
      {
        prevActionResult: Record<string, any>;
        action: ActionName;
        category: CategoryViewModel;
        datasets: Record<string, DatasetGridViewModel>;
        datasetId: UniqueId;
        refreshRules?: IContextMenuItemRefreshRules;
        refresh?: RefreshStrategy;
        hook?: boolean;
      }
    >(),
    processMultistepSuccess: successAction(),
    processMultistepFailure: failureAction(),
    continueMultistep: pendingAction<
      ActionsState,
      {
        action: ActionName;
        args: TActionArgsValues;
        source: CategoryFormSource;
        context: ClientContext;
        hook?: boolean;
        formId?: UniqueId;
      }
    >(),
    continueMultistepSuccess: successAction(),
    continueMultistepFailure: failureAction(),
    processShowCategory: emptyAction<{
      outParams: Record<string, any>;
      action: ActionName;
      dataset: UniqueId;
      category: CategoryName;
      dictionaryId: UniqueId;
      hook?: boolean;
      refresh?: RefreshStrategy;
      refreshRules?: IContextMenuItemRefreshRules;
    }>(),
    execute: pendingAction<
      ActionsState,
      {
        datasetId: UniqueId;
        action: string;
        editedRowContext: Readonly<Partial<Record<ClientContextKeys, unknown>>>;
        newValue: unknown;
      }
    >(),
    executeSuccess: successAction(),
    executeFailure: failureAction(),
    submitShowCategory: pendingAction<
      ActionsState,
      {
        outParams: Record<string, any>;
        action: ActionName;
        dataset: UniqueId;
        dictionaryId: UniqueId;
        hook?: boolean;
        refreshRules?: IContextMenuItemRefreshRules;
        refresh?: RefreshStrategy;
      }
    >(),
    submitShowCategorySuccess: successAction(),
    submitShowCategoryFailure: failureAction(),
    clearProcessState(
      state,
      { payload: { categoryId } }: PayloadAction<{ categoryId: UniqueId }>
    ) {
      delete state.processState[categoryId];
    },
  },
});

export const actionsReducer = actionsSlice.reducer;

export default actionsSlice.actions;
